// =============================================================================
// domain/duplicateTrades.ts — the same trade written into one account twice.
// -----------------------------------------------------------------------------
// PORTED FROM `tradecoach-saas/app/index.html:46585-46683` — semantics, not
// code (D3). `#73`.
//
// ── 🚨 WHY THIS IS A MIGRATION BLOCKER AND NOT A PARITY NICETY ──
//
// Beta puts a gold banner on the Accounts tab when it finds these, with a
// review-and-remove tool behind it. **Josh's book has three live groups in it
// today** (screenshot, 2026-09-04), and every screen that counts trades is
// inflated by them. A client that cannot see or remove them means the duplicates
// survive the switch, silently, with no route to correct them.
//
// ── 🚨 WHAT THIS TOOL IS NOT FOR, learned on the real book (D143) ──
//
// It removes a ROW that was written twice. It cannot fix TWO LOGGERS pointed at
// one broker account: Sierra and ATAS both record the whole session and split
// the same position into different numbers of fills, so there is no row-to-row
// correspondence to find. On 2026-08-05 that produced 65 rows from three sources
// for one session and a day that read −$130 when it was −$1,462. The cure there
// is removing one IMPORT BATCH — Trades → Import history → Undo import, which
// this client has — not picking rows off one at a time.
//
// ⚠ AND THE OPPOSITE MISTAKE IS WORSE. D143 also found an ATAS export that was
// MISSING the largest loss of the day. So nothing here may prefer a "file" copy
// over a live one on principle, and nothing may delete without the trader
// confirming the group. An export is not a backup.
// =============================================================================

import { DEMO_PREFIX } from './demo';

/** The fields the signature is built from — deliberately the narrowest read. */
export interface DupSigTrade {
  id: string;
  accountId: string | null;
  date: string | null;
  timeIn: string | null;
  contract: string | null;
  side: string | null;
  qty: number | null;
  entry: number | null;
  exit: number | null;
}

/**
 * A trade's identity for duplicate purposes: instant, instrument, direction,
 * SIZE and both fill prices.
 *
 * Returns `null` when any part is missing, and the caller must then skip the
 * trade entirely.
 *
 * ⚠ `null` IS NOT A KEY. Two half-parsed trades both missing prices would agree
 * on "undefined|undefined" and match as duplicates — which is exactly the false
 * positive this guard exists to stop, and it would propose deleting one of two
 * unrelated trades.
 *
 * ⚠ QTY IS PART OF THE KEY (D142). Without it, a 3-lot and a 7-lot at the same
 * instant and price are "the same trade", and the tool offers to delete the
 * bigger one.
 */
export function tradeSignature(t: DupSigTrade): string | null {
  const { date, timeIn, contract, side, qty, entry, exit } = t;
  if (!date || !timeIn || !contract || !side) return null;
  if (qty == null || entry == null || exit == null) return null;
  return [date, timeIn, contract, side, qty, entry, exit].join('|');
}

export interface DupGroup<T extends DupSigTrade> {
  /** `accountId||signature` — stable, so a UI can key a row on it. */
  key: string;
  accountId: string;
  trades: readonly T[];
}

/**
 * Groups of trades WITHIN one account sharing a signature. Two or more is a
 * candidate.
 *
 * ⚠ WITHIN ONE ACCOUNT ONLY. The same signature across two accounts is the
 * account-MERGE tool's job — the trader may genuinely hold the same position on
 * a combine and its funded twin, and deleting one of those is deleting real
 * money from a real account.
 *
 * ⚠ CANDIDATES, NOT DUPLICATES, and the distinction is load-bearing all the way
 * to the screen. Two same-minute, same-size scalps at the same price are a
 * legitimate pair. Nothing here decides; it proposes, per group, and the trader
 * unticks the ones that are real.
 *
 * ⚠ THE DEMO BOOK IS SKIPPED HERE, not by the caller. Its rows are generated
 * from one seed and can repeat by construction, so a demo user would be shown a
 * banner offering to delete trades that were never theirs — and the rule that
 * they are generated is a fact about the data, not about the screen.
 *
 * @param skipAccountIds any FURTHER accounts to leave alone.
 */
export function detectDuplicateTrades<T extends DupSigTrade>(
  trades: readonly T[],
  opts: { skipAccountIds?: ReadonlySet<string> } = {},
): DupGroup<T>[] {
  const skip = opts.skipAccountIds ?? new Set<string>();
  const groups = new Map<string, T[]>();

  for (const t of trades) {
    const acct = String(t.accountId ?? '');
    if (!acct || acct.startsWith(DEMO_PREFIX) || skip.has(acct)) continue;
    const sig = tradeSignature(t);
    if (sig === null) continue;
    const key = `${acct}||${sig}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(t);
    else groups.set(key, [t]);
  }

  const out: DupGroup<T>[] = [];
  for (const [key, ts] of groups) {
    if (ts.length < 2) continue;
    out.push({ key, accountId: key.slice(0, key.indexOf('||')), trades: ts });
  }
  return out;
}

/** How many trades would go if every group were accepted. */
export function duplicateExcess<T extends DupSigTrade>(
  groups: readonly DupGroup<T>[],
): number {
  return groups.reduce((n, g) => n + g.trades.length - 1, 0);
}

// ---------------------------------------------------------------------------
// Which copy survives
// ---------------------------------------------------------------------------

/** Everything that makes one copy worth more than another. */
export interface DupRichTrade extends DupSigTrade {
  notes?: string | null;
  patternId?: string | null;
  exitModelId?: string | null;
  grade?: string | null;
  sessionLabel?: string | null;
  marketRegime?: string | null;
  newsEvent?: string | null;
  mistakeTags?: readonly string[] | null;
  confluences?: Record<string, unknown> | null;
  booleans?: Record<string, unknown> | null;
  screenshots?: readonly unknown[] | null;
  recordings?: readonly unknown[] | null;
  maePoints?: number | null;
  mfePoints?: number | null;
  maeSecs?: number | null;
  mfeSecs?: number | null;
  executions?: readonly unknown[] | null;
}

const filled = (v: unknown): boolean =>
  v !== null && v !== undefined && String(v).trim() !== '';

/**
 * How much would be LOST by deleting this copy.
 *
 * ── 🚨 IT IS NOT A REWARD FOR TYPING ──
 *
 * The old client scored twelve things the trader typed and nothing the import
 * brought in — so a copy carrying a full excursion walk and a per-fill timeline
 * scored ZERO and lost to a copy with one tag on it. That record powers the
 * whole Excursion tab, half of Setup Quality and every give-back number, and it
 * is the part that is IMPOSSIBLE to re-enter by hand: excursion comes from paid
 * market data, the fill timeline from an export the trader may no longer have.
 *
 * Both halves count here, for that reason.
 */
export function tradeRichness(t: DupRichTrade): number {
  let n = 0;
  if (filled(t.notes)) n += 1;
  if (filled(t.patternId)) n += 1;
  if (filled(t.exitModelId)) n += 1;
  if (filled(t.grade)) n += 1;
  if (filled(t.sessionLabel)) n += 1;
  if (filled(t.marketRegime)) n += 1;
  if (filled(t.newsEvent)) n += 1;
  if ((t.mistakeTags ?? []).length) n += 1;

  // ⚠ A confluence is `{active: bool}` per id — the presence of the key is not
  // the trader having ticked it, and counting keys would score an untouched
  // registry as annotation.
  const conf = t.confluences ?? {};
  if (Object.values(conf).some(
    (v) => !!v && typeof v === 'object' && (v as { active?: unknown }).active === true,
  )) n += 1;

  // ⚠ `=== true` — a discipline flag the trader answered NO to is stored as
  // false (the asymmetry recorded against `preMarketPlanFollowed`), and a falsy
  // check would count it.
  if (Object.values(t.booleans ?? {}).some((v) => v === true)) n += 1;

  if ((t.screenshots ?? []).length) n += 1;
  if ((t.recordings ?? []).length) n += 1;

  if (t.maePoints != null) n += 1;
  if (t.mfePoints != null) n += 1;
  if (t.maeSecs != null || t.mfeSecs != null) n += 1;
  if ((t.executions ?? []).length) n += 1;
  return n;
}

export interface DedupePlan {
  keepId: string;
  removeIds: string[];
}

/**
 * For one group: which copy to keep, and which to remove.
 *
 * ── 🚨 THE TIE-BREAK USED TO BE IMPORT ORDER, WHICH IS NOT A REASON ──
 *
 * The old ranking was richness, then position in the trades array — i.e.
 * whichever file happened to be imported first. On the beta user's two flagged
 * groups both copies carried exactly one annotation, so the tie-break decided
 * everything, and it kept the 3-lot over the 7-lot and the 2-lot over the 6-lot.
 *
 * Order:
 *   1. richness      — lose the least
 *   2. contracts     — a copy describing more of the position wins
 *   3. array order   — last, and only so the answer is deterministic
 *
 * ⚠ Size never decides alone and cannot: `tradeSignature` includes qty, so two
 * rows in one group ALREADY agree on it. That rung is a belt to the braces —
 * it only bites on a group formed by a future widening of the key.
 */
export function planDedupeGroup<T extends DupRichTrade>(
  trades: readonly T[],
): DedupePlan {
  const order = new Map(trades.map((t, i) => [t.id, i]));
  const ranked = trades.slice().sort((a, b) =>
    (tradeRichness(b) - tradeRichness(a))
    || ((Number(b.qty) || 0) - (Number(a.qty) || 0))
    || ((order.get(a.id) ?? 0) - (order.get(b.id) ?? 0)));

  // ⚠ A group always has at least two members — `detectDuplicateTrades` drops
  // singletons — but this is written so a caller passing one row gets "keep it,
  // remove nothing" rather than an undefined id in a delete list.
  const keep = ranked[0];
  return {
    keepId: keep ? keep.id : '',
    removeIds: ranked.slice(1).map((t) => t.id),
  };
}
